/**
 * Salunas analytics pings for the operator console.
 *
 * Only two events are reported from the SPA — a page view on every route
 * change and "booking dialog opened" — the rest (search, salon view, booking
 * completed) is recorded server-side by the endpoints that already handle
 * them. The backend derives the domain from the proxied request host, so the
 * payload carries no host and no visitor identity. Fire-and-forget: a failed
 * ping is silently dropped, never retried, never surfaced.
 */
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'https://business.clever-booking.com';

export type MarketplaceClientEvent = 'PAGE_VIEW' | 'BOOKING_STARTED';

/**
 * True while the address bar carries a magic-link `token=`: no analytics ping
 * leaves the page then, so the single-use token never travels in a Referer
 * or beacon (the login page strips it from the URL right after reading it).
 */
export function urlCarriesToken(search: string = typeof window !== 'undefined' ? window.location.search : ''): boolean {
  return /[?&]token=/.test(search);
}

export function trackMarketplaceEvent(
  event: MarketplaceClientEvent,
  subdomain?: string,
): void {
  if (urlCarriesToken()) return;
  try {
    void fetch(`${API_BASE_URL}/api/v1/marketplace/events`, {
      method: 'POST',
      // keepalive lets the ping survive an immediate navigation.
      keepalive: true,
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(subdomain ? { event, subdomain } : { event }),
    }).catch(() => undefined);
  } catch {
    // fetch itself can throw in exotic embeds — analytics never breaks the page.
  }
}
